import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { store } from './store';
import { setupInterceptors } from './config/api';
import { getCurrentUser } from './store/slices/authSlice';
import { setDarkMode } from './store/slices/uiSlice';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import PasswordResetRequest from './components/auth/PasswordResetRequest';
import SetNewPassword from './components/auth/SetNewPassword';
import PrivacyPolicyPage from './components/legal/PrivacyPolicyPage';
import TermsOfServicePage from './components/legal/TermsOfServicePage';
import SupportPage from './components/legal/SupportPage';
import AppMarketingPage from './components/legal/AppMarketingPage';
import LandingPage from './components/landing/LandingPage';
import PublicCartPage from './components/cart/PublicCartPage';
import ChatApp from './components/chat/ChatApp';
import AdminPanel from './components/admin/AdminPanel';
import AdminMemberProfile from './components/admin/AdminMemberProfile';
import Profile from './components/profile/Profile';
import AnnouncementPanel from './components/announcements/AnnouncementPanel';
import AnnouncementPopup from './components/announcements/AnnouncementPopup';
import EventInvitePopup from './features/events/components/EventInvitePopup';
import RelationshipNudgePopup from './components/common/RelationshipNudgePopup';
import MorningDigestPopup from './components/common/MorningDigestPopup';
import TaskManagementPanel from './components/tasks/TaskManagementPanel';
import { VoiceTaskDialogHost } from './components/tasks/TaskCreationDialog';
import CaptionActionConfirmHost from './components/common/CaptionActionConfirmHost';
import { VoiceWorkflowDialogHost } from './components/chat/CreateWorkflowDialog';
import IntegrationsPage from './components/integrations/IntegrationsPage';
import SavedMessagesPage from './components/savedMessages/SavedMessagesPage';
import ProtectedRoute from './components/common/ProtectedRoute';
import NotificationContainer from './components/common/NotificationContainer';
import DraggableNotificationBubble from './components/common/DraggableNotificationBubble';
import DraggableAudioCaptionBubble from './components/common/DraggableAudioCaptionBubble';
import TaskArrivalToast from './components/common/TaskArrivalToast';
import LiveTrackingAlertToast from './components/liveTracking/LiveTrackingAlertToast';
import TabSessionManager from './components/common/TabSessionManager';
import SessionClosedPage from './components/common/SessionClosedPage';
import LiveKitCallPage from './components/chat/LiveKitCallPage';
import TeamAttendanceAnalyticsPage from './components/attendance/TeamAttendanceAnalyticsPage';
import activityService from './services/activityService';
import { startPresenceIdleWatcher, stopPresenceIdleWatcher } from './services/presenceIdleService';
import websocketService from './services/websocketService';
import tabSessionService from './services/tabSessionService';
import {
  startRealmSync,
  wireRealmWebSocketWriters,
  isRealmEnabled,
  isElectronRuntime,
} from './realm';
import { flushOutboundMessages } from './store/slices/chatSlice';
import { flushOutboundTasks } from './store/slices/taskSlice';
import { applyWindowTitle } from './utils/windowTitle';
import { isDesktopCallWindow, isLiveKitCallPath } from './utils/liveKitCallWindow';
import { forceLeaveCallPresence, startStaleCallPresenceWatch } from './utils/callPresence';
import DesktopBootOverlay from './components/common/DesktopBootOverlay';
import DesktopMandatoryUpdateGate from './components/common/DesktopMandatoryUpdateGate';
import { emitDesktopBoot } from './utils/desktopBoot';
import './index.css';

/** Electron loads UI from file:// — HashRouter avoids broken deep links. */
const Router = isElectronRuntime() ? HashRouter : BrowserRouter;

/** After 15 minutes idle, land on /chat so Today's tasks can open in extended mode. */
function IdleTodayTasksRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const go = (event) => {
      if (!event?.detail?.focusTasks) return;
      if (location.pathname.startsWith('/call')) return;
      if (location.pathname !== '/chat') {
        navigate('/chat', { replace: true });
      }
    };
    window.addEventListener('workloop:open-daily-tasks', go);
    return () => window.removeEventListener('workloop:open-daily-tasks', go);
  }, [location.pathname, navigate]);

  return null;
}


// Initialize Axios interceptors
setupInterceptors(store, {
  onTokenRefreshed: (accessToken) => websocketService.updateToken(accessToken),
});

// Wire WS → Realm writers once (no-ops when Realm disabled)
wireRealmWebSocketWriters(websocketService);
const TabSessionActiveGate = ({ children }) => {
  const [tabSessionState, setTabSessionState] = React.useState(() => tabSessionService.getState());

  React.useEffect(() => tabSessionService.subscribe(setTabSessionState), []);

  // Desktop is a single window and never claims the browser tab lock (isLeader stays false).
  if (isElectronRuntime()) {
    if (isDesktopCallWindow() || isLiveKitCallPath()) {
      return null;
    }
    return children;
  }

  const isTabActive = tabSessionState.isLeader
    && !tabSessionState.isSuperseded
    && !tabSessionState.showSessionConflict;

  if (!isTabActive) {
    return null;
  }

  return children;
};

function AppContent() {
  const dispatch = useDispatch();
  const { isAuthenticated, token, user } = useSelector((state) => state.auth);
  const { darkMode } = useSelector((state) => state.ui);

  useEffect(() => {
    if (isLiveKitCallPath()) return undefined;
    applyWindowTitle(isAuthenticated ? user : null);
    return undefined;
  }, [isAuthenticated, user?.firstName, user?.lastName, user?.username]);

  useEffect(() => {
    if (!token) {
      const savedDarkMode = localStorage.getItem('darkMode') === 'true';
      dispatch(setDarkMode(savedDarkMode));
      return undefined;
    }

    const fetchUserIfActive = () => {
      if (!user && (tabSessionService.isActiveTab() || isElectronRuntime())) {
        dispatch(getCurrentUser());
      }
    };

    fetchUserIfActive();
    return tabSessionService.subscribe(fetchUserIfActive);
  }, [dispatch, token, user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!isAuthenticated || !tabSessionService.isActiveTab()) return;
      
      if (document.visibilityState === 'visible') {
        activityService.trackTabActive();
      } else {
        activityService.trackTabInactive();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      stopPresenceIdleWatcher();
      return undefined;
    }
    startPresenceIdleWatcher();
    return () => stopPresenceIdleWatcher();
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (!isAuthenticated || isDesktopCallWindow() || isLiveKitCallPath()) {
      return undefined;
    }
    const stopWatch = startStaleCallPresenceWatch();
    let unsubscribeEnded = () => {};
    try {
      unsubscribeEnded = window.workloopRealm?.onCallEnded?.(() => {
        forceLeaveCallPresence();
      }) || (() => {});
    } catch {
      unsubscribeEnded = () => {};
    }
    return () => {
      stopWatch();
      unsubscribeEnded();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isDesktopCallWindow() || isLiveKitCallPath()) {
      emitDesktopBoot({ percent: 100, done: true, label: 'Ready', detail: 'Opening Workloop' });
      return undefined;
    }
    if (!isElectronRuntime()) {
      return undefined;
    }
    if (!isAuthenticated || !token) {
      emitDesktopBoot({ percent: 100, done: true, label: 'Ready', detail: 'Opening Workloop' });
      return undefined;
    }
    if (!user?.id) {
      emitDesktopBoot({ percent: 20, label: 'Syncing...', detail: 'Restoring session' });
      return undefined;
    }
    if (!isRealmEnabled()) {
      emitDesktopBoot({ percent: 100, done: true, label: 'Ready', detail: 'Opening Workloop' });
      return undefined;
    }
    let cancelled = false;
    emitDesktopBoot({ percent: 14, label: 'Syncing...', detail: 'Connecting workspace' });
    startRealmSync(user.id).catch((err) => {
      if (!cancelled) {
        console.warn('[realm] start failed', err);
        emitDesktopBoot({
          percent: 100,
          done: true,
          label: 'Ready',
          detail: 'Opening Workloop',
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onOnline = () => {
      dispatch(flushOutboundTasks());
      if (isRealmEnabled()) {
        dispatch(flushOutboundMessages());
      }
      import('./realm/outboundQueue').then(({ flushOutboundReminderQueue }) => {
        flushOutboundReminderQueue();
      }).catch(() => {});
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [dispatch, isAuthenticated]);

  return (
    <DesktopMandatoryUpdateGate>
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
        <DesktopBootOverlay />
        <Router>
          <IdleTodayTasksRedirect />
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? <Navigate to="/chat" replace /> : <Login />
              }
            />
            <Route
              path="/register"
              element={
                isAuthenticated ? <Navigate to="/chat" replace /> : <Register />
              }
            />
            <Route
              path="/password-reset-request"
              element={
                isAuthenticated ? <Navigate to="/chat" replace /> : <PasswordResetRequest />
              }
            />
            <Route
              path="/set-password"
              element={<SetNewPassword />}
            />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/app" element={<AppMarketingPage />} />
            <Route path="/cart/:token" element={<PublicCartPage />} />
            <Route path="/session-closed" element={<SessionClosedPage />} />
            <Route
              path="/call"
              element={
                <ProtectedRoute>
                  <LiveKitCallPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/attendance/analytics"
              element={
                <ProtectedRoute>
                  <TeamAttendanceAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatApp />
                </ProtectedRoute>
              }
            >
              <Route path="tasks" element={<TaskManagementPanel />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute>
                  <AdminMemberProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/announcements"
              element={
                <ProtectedRoute>
                  <AnnouncementPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/task-management"
              element={<Navigate to="/chat/tasks" replace />}
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <IntegrationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jira"
              element={<Navigate to="/integrations?plugin=jira" replace />}
            />
            <Route
              path="/cumin"
              element={<Navigate to="/integrations?plugin=cumin" replace />}
            />
            <Route
              path="/saved-messages"
              element={
                <ProtectedRoute>
                  <SavedMessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                isDesktopCallWindow()
                  ? <LiveKitCallPage />
                  : isElectronRuntime()
                    ? (
                      isAuthenticated
                        ? <Navigate to="/chat" replace />
                        : <Navigate to="/login" replace />
                    )
                    : <LandingPage />
              }
            />
            {isElectronRuntime() && !isDesktopCallWindow() ? (
              <Route
                path="*"
                element={<Navigate to={isAuthenticated ? '/chat' : '/login'} replace />}
              />
            ) : null}
          </Routes>
          {isAuthenticated && (
            <TabSessionActiveGate>
              <AnnouncementPopup />
              <EventInvitePopup />
              <RelationshipNudgePopup />
              <MorningDigestPopup />
            </TabSessionActiveGate>
          )}
          <TabSessionActiveGate>
            <DraggableNotificationBubble />
            <DraggableAudioCaptionBubble />
            {isAuthenticated && <VoiceTaskDialogHost />}
            {isAuthenticated && <CaptionActionConfirmHost />}
            {isAuthenticated && <VoiceWorkflowDialogHost />}
            {isAuthenticated && <TaskArrivalToast />}
            {isAuthenticated && <LiveTrackingAlertToast />}
          </TabSessionActiveGate>
          <TabSessionManager
            isAuthenticated={isAuthenticated}
            token={token}
          />
        </Router>
        <NotificationContainer />
      </div>
    </div>
    </DesktopMandatoryUpdateGate>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;

